<template>
  <div
    v-if="loading"
    class="flex justify-center items-center py-8">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
  </div>

  <div
    v-else-if="!geogridConfigs || geogridConfigs.length === 0"
    class="text-center py-8">
    <div class="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4">
      <p class="text-amber-700">
        No geogrid configurations found for this organization.
        Set up geogrids for locations to view analytics.
      </p>
    </div>
  </div>

  <div
    v-else
    class="space-y-6">
    <!-- Location/Config Selection -->
    <div
      v-if="geogridConfigs.length > 1"
      class="mb-4">
      <label class="block text-sm font-medium text-gray-700 mb-2">
        Select Location
      </label>
      <select
        v-model="selectedConfigIndex"
        class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
               focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white">
        <option
          v-for="(config, index) in geogridConfigs"
          :key="config.id"
          :value="index">
          {{ config.locationName }} - {{ config.businessName }}
        </option>
      </select>
    </div>

    <!-- Selected Config Display -->
    <div
      v-if="selectedConfig"
      class="space-y-4">
      <!-- Grid Configuration Summary -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h4 class="text-md font-semibold mb-3 flex items-center">
          <i class="fas fa-cog text-gray-600 mr-2" />
          Grid Configuration
        </h4>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div class="text-sm text-gray-500">
              Grid Size
            </div>
            <div class="font-medium">
              {{ selectedConfig.gridSize || 'N/A' }}x{{ selectedConfig.gridSize || 'N/A' }}
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500">
              Distance
            </div>
            <div class="font-medium">
              {{ formatDistance(selectedConfig) }}
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500">
              Local Results
            </div>
            <div class="font-medium">
              {{ selectedConfig.local ? 'Yes' : 'No' }}
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500">
              Keywords
            </div>
            <div class="font-medium">
              {{ selectedConfig.keywords.length }}
            </div>
          </div>
        </div>
        <div class="mt-3 text-sm text-gray-500">
          Last updated: {{ formatDate(selectedConfig.lastGeogridCreatedAt) }}
        </div>
      </div>

      <!-- Overall Performance Metrics -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h4 class="text-md font-semibold mb-3 flex items-center">
          <i class="fas fa-chart-line text-indigo-600 mr-2" />
          Performance Metrics
        </h4>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="bg-gray-50 p-4 rounded-lg">
            <div class="text-sm text-gray-500">
              Average Grid Rank (AGR)
            </div>
            <div class="text-2xl font-bold">
              {{ averageAgr }}
            </div>
            <div class="text-xs text-gray-500 mt-1">
              Lower is better
            </div>
          </div>
          <div class="bg-gray-50 p-4 rounded-lg">
            <div class="text-sm text-gray-500">
              Adjusted Top Grid Rank (ATGR)
            </div>
            <div class="text-2xl font-bold">
              {{ averageAtgr }}
            </div>
          </div>
          <div class="bg-gray-50 p-4 rounded-lg">
            <div class="text-sm text-gray-500">
              Share of Local Visibility (SOLV)
            </div>
            <div class="text-2xl font-bold">
              {{ averageSolv }}%
            </div>
          </div>
        </div>
      </div>

      <!-- Timeline Analysis -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h4 class="text-md font-semibold mb-3 flex items-center">
          <i class="fas fa-chart-line text-emerald-600 mr-2" />
          Performance Timeline
        </h4>

        <!-- Metric Type Selector -->
        <div class="flex gap-2 mb-4">
          <button
            v-for="metric in ['agr', 'atgr', 'solv']"
            :key="metric"
            class="px-3 py-1 text-sm rounded-md transition-colors"
            :class="selectedMetric === metric
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
            @click="selectedMetric = metric">
            {{ metric.toUpperCase() }}
          </button>
        </div>

        <div v-if="keywordsWithHistory.length > 0">
          <!-- Aggregate Chart -->
          <div class="mb-6">
            <h5 class="text-sm font-medium text-gray-700 mb-3">
              Aggregate Performance - {{ selectedMetric.toUpperCase() }}
            </h5>
            <div
              class="relative"
              style="height: 250px;">
              <canvas ref="aggregateChartCanvas" />
            </div>
          </div>

          <!-- Individual Keyword Charts -->
          <div class="space-y-4">
            <h5 class="text-sm font-medium text-gray-700">
              Individual Keyword Performance
            </h5>
            <div
              v-for="keyword in keywordsWithHistory"
              :key="`chart-${keyword.id}-${selectedMetric}`"
              class="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div class="mb-2">
                <span class="font-medium text-sm">{{ keyword.text }}</span>
                <span class="text-xs text-gray-500 ml-2">
                  Current: {{ formatMetric(keyword[selectedMetric]) }}{{ selectedMetric === 'solv' ? '%' : '' }}
                </span>
              </div>
              <div
                class="relative"
                style="height: 150px;">
                <canvas :ref="el => setKeywordChartRef(keyword.id, el)" />
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="text-center py-8 text-gray-500">
          No historical data available for timeline analysis
        </div>
      </div>

      <!-- Keywords Table -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div class="flex items-center justify-between mb-4">
          <h4 class="text-md font-semibold flex items-center">
            <i class="fas fa-key text-violet-600 mr-2" />
            Keyword Performance
          </h4>
          <div class="flex items-center space-x-4">
            <!-- Filter Dropdown -->
            <select
              v-model="keywordFilter"
              class="text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
              <option value="all">
                All Keywords
              </option>
              <option value="active">
                Active Only
              </option>
              <option value="inactive">
                Inactive Only
              </option>
            </select>
            <!-- Legend -->
            <div class="flex items-center space-x-3 text-xs">
              <span class="flex items-center">
                <span class="w-2 h-2 bg-emerald-500 rounded-full mr-1" />
                Active
              </span>
              <span class="flex items-center">
                <span class="w-2 h-2 bg-amber-500 rounded-full mr-1" />
                Remote Inactive
              </span>
              <span class="flex items-center">
                <span class="w-2 h-2 bg-gray-300 rounded-full mr-1" />
                Inactive
              </span>
            </div>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="text-xs text-gray-600 bg-gray-50 border-b border-gray-200">
              <tr>
                <th
                  scope="col"
                  class="px-4 py-3 text-left font-semibold">
                  <div class="flex items-center">
                    <i class="fas fa-circle text-gray-400 mr-2 text-xs" />
                    Keyword
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-4 py-3 text-center font-semibold">
                  <div class="flex items-center justify-center">
                    <i class="fas fa-chart-line text-gray-400 mr-1 text-xs" />
                    AGR
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-4 py-3 text-center font-semibold">
                  <div class="flex items-center justify-center">
                    <i class="fas fa-chart-area text-gray-400 mr-1 text-xs" />
                    ATGR
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-4 py-3 text-center font-semibold">
                  <div class="flex items-center justify-center">
                    <i class="fas fa-percentage text-gray-400 mr-1 text-xs" />
                    SOLV
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-4 py-3 text-center font-semibold">
                  <div class="flex items-center justify-center">
                    <i class="fas fa-chart-bar text-gray-400 mr-1 text-xs" />
                    Trend
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-4 py-3 text-center font-semibold">
                  <div class="flex items-center justify-center">
                    <i class="fas fa-clock text-gray-400 mr-1 text-xs" />
                    Updated
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-4 py-3 text-center font-semibold">
                  <div class="flex items-center justify-center">
                    <i class="fas fa-tools text-gray-400 mr-1 text-xs" />
                    Actions
                  </div>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="keyword in filteredKeywords"
                :key="keyword.id"
                class="transition-colors duration-150"
                :class="{
                  'bg-white hover:bg-gray-50': keyword.active,
                  'bg-gray-50 hover:bg-gray-100 opacity-75': !keyword.active
                }">
                <td class="px-4 py-3">
                  <div class="flex items-center">
                    <span
                      class="w-2 h-2 rounded-full mr-3 flex-shrink-0"
                      :class="{
                        'bg-emerald-500': keyword.active && getProp(keyword, 'remoteActive', 'remote_active'),
                        'bg-amber-500': keyword.active && !getProp(keyword, 'remoteActive', 'remote_active'),
                        'bg-gray-300': !keyword.active
                      }" />
                    <div>
                      <div class="font-medium text-gray-900">
                        {{ keyword.text }}
                      </div>
                      <div class="text-xs text-gray-500 mt-0.5">
                        <span v-if="keyword.active && getProp(keyword, 'remoteActive', 'remote_active')">
                          Active • Remote Active
                        </span>
                        <span v-else-if="keyword.active && !getProp(keyword, 'remoteActive', 'remote_active')">
                          Active • Remote Inactive
                        </span>
                        <span v-else>
                          Inactive
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-3 text-center">
                  <div
                    class="inline-flex items-center justify-center px-2 py-1 rounded-md text-xs font-medium"
                    :class="getMetricClass(keyword.agr, 'agr')">
                    {{ formatMetric(keyword.agr) }}
                  </div>
                </td>
                <td class="px-4 py-3 text-center">
                  <div
                    class="inline-flex items-center justify-center px-2 py-1 rounded-md text-xs font-medium"
                    :class="getMetricClass(keyword.atgr, 'atgr')">
                    {{ formatMetric(keyword.atgr) }}
                  </div>
                </td>
                <td class="px-4 py-3 text-center">
                  <div
                    class="inline-flex items-center justify-center px-2 py-1 rounded-md text-xs font-medium"
                    :class="getMetricClass(keyword.solv, 'solv')">
                    {{ formatMetric(keyword.solv) }}%
                  </div>
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center justify-center">
                    <Sparkline
                      v-if="(keyword.historicalData || keyword.historical_data) &&
                        (keyword.historicalData || keyword.historical_data).length > 1"
                      :data="getSparklineData(keyword, 'solv')"
                      :width="60"
                      :height="20"
                      :color="getMetricColor('solv')"
                      :stroke-width="1.5"
                      :show-dot="false" />
                    <span
                      v-else
                      class="text-xs text-gray-400">—</span>
                  </div>
                </td>
                <td class="px-4 py-3 text-center">
                  <div class="text-xs text-gray-600">
                    {{ keyword.lastResult ? formatRelativeDate(keyword.lastResult.finishedAt) : 'Never' }}
                  </div>
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center justify-center space-x-1">
                    <button
                      v-if="keyword.lastResult && keyword.lastResult.imageUrl"
                      class="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50
                             border border-blue-200 rounded hover:bg-blue-100 transition-colors duration-150"
                      @click="viewGeogridImage(keyword, false)">
                      <i class="fas fa-map text-xs mr-1" />
                      Map
                    </button>
                    <button
                      v-if="keyword.lastResult && keyword.lastResult.headlessImageUrl"
                      class="inline-flex items-center px-2 py-1 text-xs font-medium text-violet-700 bg-violet-50
                             border border-violet-200 rounded hover:bg-violet-100 transition-colors duration-150"
                      @click="viewGeogridImage(keyword, true)">
                      <i class="fas fa-eye-slash text-xs mr-1" />
                      Raw
                    </button>
                    <span
                      v-if="!keyword.lastResult || (!keyword.lastResult.imageUrl && !keyword.lastResult.headlessImageUrl)"
                      class="text-xs text-gray-400">
                      No data
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Summary Stats -->
        <div class="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between text-xs text-gray-600">
          <div>
            Total Keywords: <span class="font-semibold text-gray-900">{{ selectedConfig.keywords.length }}</span>
          </div>
          <div class="flex items-center space-x-4">
            <div>
              Active: <span class="font-semibold text-emerald-600">{{ activeKeywordsCount }}</span>
            </div>
            <div>
              Inactive: <span class="font-semibold text-gray-600">{{ inactiveKeywordsCount }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Geogrid Image Modal -->
      <div
        v-if="selectedKeyword"
        class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div class="flex justify-between items-center mb-3">
          <h4 class="text-md font-semibold">
            Geogrid Map: {{ selectedKeyword.text }}
            {{ isHeadless ? '(Headless)' : '' }}
          </h4>
          <button
            class="text-gray-500 hover:text-gray-700"
            @click="closeImage">
            <i class="fas fa-times" />
          </button>
        </div>

        <img
          :src="currentImageUrl"
          :alt="`Geogrid visualization for ${selectedKeyword.text}`"
          class="w-full rounded-md mb-2">

        <div class="flex justify-between items-center text-sm text-gray-500">
          <div>
            Last updated: {{ formatDate(selectedKeyword.lastResult.finishedAt) }}
          </div>
          <div v-if="canToggleHeadless">
            <button
              class="px-3 py-1 text-sm rounded-md bg-gray-200 hover:bg-gray-300 transition-colors"
              @click="toggleHeadless">
              {{ isHeadless ? 'Show Normal' : 'Show Headless' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, onMounted, nextTick, watch
} from 'vue'
import axios from 'axios'
import { Chart, registerables } from 'chart.js'
import Sparkline from './components/sparkline.vue'

Chart.register(...registerables)

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// State
const loading = ref(true)
const geogridConfigs = ref([])
const selectedConfigIndex = ref(0)
const selectedKeyword = ref(null)
const isHeadless = ref(false)
const selectedMetric = ref('solv') // Default to SOLV for timeline view
const keywordFilter = ref('all') // Filter for active/inactive keywords

// Chart refs
const aggregateChartCanvas = ref(null)
const keywordChartRefs = ref({})
const aggregateChartInstance = ref(null)
const keywordChartInstances = ref({})

// Helper to access properties in both camelCase and snake_case
const getProp = (obj, camelCase, snakeCase) => (obj[camelCase] !== undefined ? obj[camelCase] : obj[snakeCase])

// Computed
const selectedConfig = computed(() => {
  const config = geogridConfigs.value[selectedConfigIndex.value]
  if (!config) return null

  // Normalize the config to handle both camelCase and snake_case
  return {
    ...config,
    id: config.id,
    locationName: getProp(config, 'locationName', 'location_name'),
    businessName: getProp(config, 'businessName', 'business_name'),
    gridSize: getProp(config, 'gridSize', 'grid_size'),
    gridPointDistance: getProp(config, 'gridPointDistance', 'grid_point_distance'),
    gridDistanceMeasure: getProp(config, 'gridDistanceMeasure', 'grid_distance_measure'),
    local: config.local,
    lastGeogridCreatedAt: getProp(config, 'lastGeogridCreatedAt', 'last_geogrid_created_at'),
    keywords: config.keywords
  }
})

const averageAgr = computed(() => {
  if (!selectedConfig.value || !selectedConfig.value.keywords.length) return 'N/A'
  const validKeywords = selectedConfig.value.keywords.filter((k) => k.agr)
  if (!validKeywords.length) return 'N/A'
  const sum = validKeywords.reduce((acc, k) => acc + parseFloat(k.agr), 0)
  return (sum / validKeywords.length).toFixed(2)
})

const averageAtgr = computed(() => {
  if (!selectedConfig.value || !selectedConfig.value.keywords.length) return 'N/A'
  const validKeywords = selectedConfig.value.keywords.filter((k) => k.atgr)
  if (!validKeywords.length) return 'N/A'
  const sum = validKeywords.reduce((acc, k) => acc + parseFloat(k.atgr), 0)
  return (sum / validKeywords.length).toFixed(2)
})

const averageSolv = computed(() => {
  if (!selectedConfig.value || !selectedConfig.value.keywords.length) return 'N/A'
  const validKeywords = selectedConfig.value.keywords.filter((k) => k.solv)
  if (!validKeywords.length) return 'N/A'
  const sum = validKeywords.reduce((acc, k) => acc + parseFloat(k.solv), 0)
  return (sum / validKeywords.length).toFixed(2)
})

const canToggleHeadless = computed(() => {
  if (!selectedKeyword.value || !selectedKeyword.value.lastResult) return false
  return !!selectedKeyword.value.lastResult.imageUrl &&
         !!selectedKeyword.value.lastResult.headlessImageUrl
})

const currentImageUrl = computed(() => {
  if (!selectedKeyword.value || !selectedKeyword.value.lastResult) return ''

  if (isHeadless.value && selectedKeyword.value.lastResult.headlessImageUrl) {
    return selectedKeyword.value.lastResult.headlessImageUrl
  }

  return selectedKeyword.value.lastResult.imageUrl
})

const keywordsWithHistory = computed(() => {
  if (!selectedConfig.value || !selectedConfig.value.keywords) return []

  return selectedConfig.value.keywords.filter((k) => {
    const historicalData = getProp(k, 'historicalData', 'historical_data')
    return historicalData && historicalData.length > 1
  })
})

const activeKeywordsCount = computed(() => {
  if (!selectedConfig.value || !selectedConfig.value.keywords) return 0
  return selectedConfig.value.keywords.filter((k) => k.active).length
})

const inactiveKeywordsCount = computed(() => {
  if (!selectedConfig.value || !selectedConfig.value.keywords) return 0
  return selectedConfig.value.keywords.filter((k) => !k.active).length
})

const filteredKeywords = computed(() => {
  if (!selectedConfig.value || !selectedConfig.value.keywords) return []

  const keywords = selectedConfig.value.keywords

  switch (keywordFilter.value) {
    case 'active':
      return keywords.filter((k) => k.active)
    case 'inactive':
      return keywords.filter((k) => !k.active)
    default:
      return keywords
  }
})

// Define all methods before they are used in computed properties
const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatMetric = (value) => {
  if (!value) return 'N/A'
  return parseFloat(value).toFixed(2)
}

const formatDistance = (config) => {
  const gridPointDistance = getProp(config, 'gridPointDistance', 'grid_point_distance')
  const gridDistanceMeasure = getProp(config, 'gridDistanceMeasure', 'grid_distance_measure')

  if (!gridPointDistance) return 'N/A'

  // Check if distance is in meters and convert to miles
  if (gridDistanceMeasure === 'meters' || gridPointDistance > 100) {
    // Convert meters to miles (1 mile = 1609.34 meters)
    const miles = gridPointDistance / 1609.34
    return `${miles.toFixed(1)} mi`
  }

  // Otherwise assume it's already in miles
  return `${gridPointDistance} mi`
}

const getSparklineData = (keyword, metric) => {
  const historicalData = getProp(keyword, 'historicalData', 'historical_data')
  if (!historicalData || historicalData.length === 0) return []

  return historicalData
    .filter((d) => d[metric] !== null && d[metric] !== undefined)
    .map((d) => parseFloat(d[metric]))
}

const getMetricColor = (metric) => {
  switch (metric) {
    case 'agr':
      return '#F05252' // red-500 - lower is better
    case 'atgr':
      return '#E3A008' // amber-400
    case 'solv':
      return '#0E9F6E' // emerald-500 - higher is better
    default:
      return '#3F83F8' // blue-500
  }
}

const getMetricClass = (value, metric) => {
  if (!value || value === 'N/A') return 'bg-gray-100 text-gray-600'

  const numValue = parseFloat(value)

  if (metric === 'agr') {
    // Lower is better for AGR
    if (numValue <= 3) return 'bg-emerald-100 text-emerald-700'
    if (numValue <= 5) return 'bg-amber-100 text-amber-700'
    return 'bg-red-100 text-red-700'
  } if (metric === 'solv') {
    // Higher is better for SOLV
    if (numValue >= 30) return 'bg-emerald-100 text-emerald-700'
    if (numValue >= 15) return 'bg-amber-100 text-amber-700'
    return 'bg-red-100 text-red-700'
  }
  // ATGR - neutral coloring
  return 'bg-blue-100 text-blue-700'
}

const formatRelativeDate = (dateString) => {
  if (!dateString) return 'Never'

  const date = new Date(dateString)
  const now = new Date()
  const diffInMs = now - date
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

  if (diffInDays === 0) return 'Today'
  if (diffInDays === 1) return 'Yesterday'
  if (diffInDays < 7) return `${diffInDays} days ago`
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`

  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// Methods
const loadGeogridData = async() => {
  try {
    loading.value = true
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/geogrid_intelligence`, {
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      }
    })
    // Rails middleware converts snake_case to camelCase
    geogridConfigs.value = response.data.geogridConfigs || response.data.geogrid_configs || []
  } catch (error) {
    // Handle error silently or report to monitoring
  } finally {
    loading.value = false
  }
}

const viewGeogridImage = (keyword, useHeadless = false) => {
  selectedKeyword.value = keyword
  isHeadless.value = useHeadless && !!keyword.lastResult.headlessImageUrl
}

const toggleHeadless = () => {
  isHeadless.value = !isHeadless.value
}

const closeImage = () => {
  selectedKeyword.value = null
  isHeadless.value = false
}

const setKeywordChartRef = (keywordId, el) => {
  if (el) {
    keywordChartRefs.value[keywordId] = el
  }
}

const destroyCharts = () => {
  // Destroy aggregate chart
  if (aggregateChartInstance.value) {
    aggregateChartInstance.value.destroy()
    aggregateChartInstance.value = null
  }

  // Destroy keyword charts
  Object.values(keywordChartInstances.value).forEach((chart) => {
    if (chart) chart.destroy()
  })
  keywordChartInstances.value = {}
}

const renderAggregateChart = () => {
  if (!aggregateChartCanvas.value) return

  const ctx = aggregateChartCanvas.value.getContext('2d')

  // Get all unique dates from all keywords
  const allDates = new Set()
  keywordsWithHistory.value.forEach((keyword) => {
    const historicalData = getProp(keyword, 'historicalData', 'historical_data')
    historicalData.forEach((d) => {
      if (d.date) allDates.add(d.date)
    })
  })

  const sortedDates = Array.from(allDates).sort()

  // Create datasets for each keyword
  const datasets = keywordsWithHistory.value.map((keyword, index) => {
    const historicalData = getProp(keyword, 'historicalData', 'historical_data')
    const data = sortedDates.map((date) => {
      const dataPoint = historicalData.find((d) => d.date === date)
      return dataPoint ? parseFloat(dataPoint[selectedMetric.value]) : null
    })

    const colors = [
      '#3F83F8', // blue-500
      '#0E9F6E', // emerald-500
      '#E3A008', // amber-400
      '#F05252', // red-500
      '#7E3AF2', // violet-600
      '#E74694', // pink-500
      '#1C64F2', // blue-600
      '#31C48D', // emerald-400
      '#FACA15', // amber-300
      '#6875F5' // indigo-500
    ]

    return {
      label: keyword.text,
      data: data,
      borderColor: colors[index % colors.length],
      backgroundColor: `${colors[index % colors.length]}20`,
      borderWidth: 2,
      tension: 0.3,
      pointRadius: 0,
      pointHoverRadius: 4,
      spanGaps: true
    }
  })

  aggregateChartInstance.value = new Chart(ctx, {
    type: 'line',
    data: {
      labels: sortedDates.map((date) => {
        const d = new Date(date)
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }),
      datasets: datasets
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 15,
            font: {
              size: 11
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.9)',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 8,
          callbacks: {
            label: (context) => {
              const value = context.parsed.y
              const suffix = selectedMetric.value === 'solv' ? '%' : ''
              return `${context.dataset.label}: ${value?.toFixed(2)}${suffix}`
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: selectedMetric.value === 'solv',
          reverse: selectedMetric.value === 'agr', // Lower is better for AGR
          grid: {
            color: 'rgba(156, 163, 175, 0.1)',
            drawBorder: false
          },
          ticks: {
            color: '#4B5563',
            font: {
              size: 11
            },
            callback(value) {
              return selectedMetric.value === 'solv' ? `${value}%` : value
            }
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#4B5563',
            font: {
              size: 11
            },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 10
          }
        }
      }
    }
  })
}

const renderKeywordChart = (keyword) => {
  const canvas = keywordChartRefs.value[keyword.id]
  if (!canvas) return

  const ctx = canvas.getContext('2d')

  const historicalData = getProp(keyword, 'historicalData', 'historical_data')
  const filteredData = historicalData
    .filter((d) => d[selectedMetric.value] !== null && d[selectedMetric.value] !== undefined)

  const labels = filteredData.map((d) => {
    const date = new Date(d.date)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  })

  const data = filteredData.map((d) => parseFloat(d[selectedMetric.value]))

  keywordChartInstances.value[keyword.id] = new Chart(ctx, {
    type: 'line',
    data: {
      labels: labels,
      datasets: [{
        data: data,
        borderColor: getMetricColor(selectedMetric.value),
        backgroundColor: `${getMetricColor(selectedMetric.value)}20`,
        borderWidth: 2,
        tension: 0.3,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 4
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.9)',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 8,
          callbacks: {
            title: (tooltipItems) => {
              const index = tooltipItems[0].dataIndex
              const dataPoint = filteredData[index]
              const date = new Date(dataPoint.date)
              return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })
            },
            label: (context) => {
              const value = context.parsed.y
              const suffix = selectedMetric.value === 'solv' ? '%' : ''
              return `${selectedMetric.value.toUpperCase()}: ${value.toFixed(2)}${suffix}`
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: selectedMetric.value === 'solv',
          reverse: selectedMetric.value === 'agr', // Lower is better for AGR
          grid: {
            color: 'rgba(156, 163, 175, 0.1)',
            drawBorder: false
          },
          ticks: {
            color: '#4B5563',
            font: {
              size: 10
            },
            callback(value) {
              return selectedMetric.value === 'solv' ? `${value}%` : value
            }
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#4B5563',
            font: {
              size: 10
            },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 7
          }
        }
      }
    }
  })
}

const renderCharts = async() => {
  await nextTick()

  destroyCharts()

  if (keywordsWithHistory.value.length === 0) return

  // Render aggregate chart
  renderAggregateChart()

  // Render individual keyword charts
  keywordsWithHistory.value.forEach((keyword) => {
    renderKeywordChart(keyword)
  })
}

// Watchers
watch(selectedMetric, () => {
  if (keywordsWithHistory.value.length > 0) {
    renderCharts()
  }
})

watch(selectedConfigIndex, () => {
  if (keywordsWithHistory.value.length > 0) {
    renderCharts()
  }
})

watch(keywordsWithHistory, () => {
  renderCharts()
})

// Lifecycle
onMounted(() => {
  loadGeogridData()
})
</script>

<style scoped>
/* Custom styles if needed */
</style>
